import React from "react";

import { cn } from "../../../common/u1/cn";
import { Input } from "../../../common/u1/Input";
import { FinancialYearTabs } from "./FinancialYearTabs";
import { FileDropzone } from "./FileDropzone";
import { MANUAL_GROUPS } from "./financialSetupFields";

/* Method 1 — enter the figures by hand, a year at a time. */
export const ManualFinancialsPanel = ({ manual, className = "" }) => {
  const {
    years,
    year,
    values,
    setField,
    document: documentUrl,
    attachDocument,
    removeDocument,
    uploading,
    filledYears,
    onNewestYear,
  } = manual;

  return (
    <div className={className}>
      <p className="m-0 text-[12.5px] text-[var(--muted)] font-sans">
        Enter one year at a time, newest first. Your entries are kept as you
        move between years.
      </p>

      {/* Transcribed from the live tooltip on the newest year's Skip: skipping it DELETES anything entered for it, and trades it for one more year of history. */}
      {onNewestYear && (
        <p className="m-0 mt-1.5 text-[12px] text-[var(--gold-deep)] font-sans">
          If {year} is skipped, any data entered for it will be deleted.
        </p>
      )}

      {/* Clickable as of F-108. The footer's Next / Previous / Skip still walk the wizard, but the strip is also a way in to any year on offer — without it, a viewer... */}
      <FinancialYearTabs
        className="mt-2.5"
        years={years}
        value={year}
        filled={filledYears}
        onSelect={manual.selectYear}
      />

      {MANUAL_GROUPS.map((group) => (
        <section
          key={group.key}
          className={cn(
            "mt-4 rounded-[14px] border border-[var(--line)] bg-white",
            "p-4 sm:p-6"
          )}
        >
          <h3 className="m-0 text-[15px] font-bold text-[var(--ink)] font-sans">
            {group.title} · {year}
          </h3>

          <div className="mt-3.5 grid grid-cols-1 gap-4 sm:grid-cols-2">
            {group.fields.map((field) => (
              <Input
                key={field.key}
                label={field.label}
                labelVariant="field"
                id={`u1-fin-${field.key}`}
                inputMode="decimal"
                autoComplete="off"
                placeholder="0.00"
                value={values[field.key] ?? ""}
                onChange={(e) => setField(field.key, e.target.value)}
              />
            ))}
          </div>

          {/* The design attaches the optional document to the cash flow card,
              which is the last one. */}
          {group.key === "cashflow" && (
            <div className="mt-4">
              <p className="m-0 mb-2 text-[13px] font-bold text-[var(--ink)] font-sans">
                Upload File (Optional)
              </p>
              {uploading ? (
                <p
                  role="status"
                  aria-live="polite"
                  className="m-0 text-[13px] text-[var(--muted)] font-sans"
                >
                  Uploading…
                </p>
              ) : documentUrl ? (
                <div
                  className={cn(
                    "flex flex-wrap items-center justify-between gap-3 rounded-[12px]",
                    "border border-[var(--line)] bg-[var(--field-inset)] px-4 py-3"
                  )}
                >
                  <a
                    href={documentUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="min-w-0 flex-1 truncate text-[13px] font-semibold text-[var(--coral)] font-sans"
                  >
                    {`Document for ${year}`}
                  </a>
                  <button
                    type="button"
                    onClick={removeDocument}
                    className={cn(
                      "rounded px-1 text-[12px] font-bold text-[var(--muted)] font-sans",
                      "transition-colors hover:text-[var(--coral-deep)]",
                      "focus-visible:outline-none focus-visible:ring-2",
                      "focus-visible:ring-[var(--coral)]"
                    )}
                  >
                    Remove
                  </button>
                </div>
              ) : (
                <FileDropzone
                  label={`Supporting document for ${year}`}
                  files={[]}
                  onChange={(next) => attachDocument(next?.[0])}
                />
              )}
            </div>
          )}
        </section>
      ))}

    </div>
  );
};

export default ManualFinancialsPanel;
